import * as React from "react";
import { createLogger } from "../../lib/logger";
import { ErrorState } from "./error-state";

// Render crashes are always logged at error level (in every environment) so they
// surface in whatever sink the logger is wired to. See docs/error-handling.md.
const log = createLogger("ErrorBoundary");

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: (props: { error: Error; reset: () => void }) => React.ReactNode;
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log the crash + the React component stack through the central logger
    // (replaces the dev-only console.error) so it can later reach a remote sink.
    log.error("Render error caught by boundary", {
      error,
      componentStack: info.componentStack,
    });
    this.props.onError?.(error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          reset: this.reset,
        });
      }
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-6">
          <ErrorState
            title="The page hit an unexpected error"
            error={this.state.error}
            onRetry={this.reset}
            retryLabel="Reload section"
          />
        </div>
      );
    }
    return this.props.children;
  }
}
